import React, { useState, useLayoutEffect } from "react";
import { auth} from "../../config/firebase";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, AuthError, OAuthProvider, User, reauthenticateWithPopup } from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import { config, getFirebaseErrorMessage } from "../../config/nova-constants";
import { createUserIfNotExists, checkUserExists } from "../../services/api/user.service";
//import { sanitizeUsername } from "../../utils/usernameValidation";
import { useAuth } from "./AuthContext";
import { useUser } from "../../contexts/UserContext";
import { Eye, EyeOff } from "lucide-react";
import { trackEvent, setUserId, setUserProperties, AUTH_EVENTS } from "../../services/analytics";
import { trackHyperDXAction, setHyperDXUser } from "../../services/observability";

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [formError, setFormError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { setAuthStatus } = useAuth();
  const { setSignupInProgress, triggerProfileFetch } = useUser();
  const [loading, setLoading] = useState(false);


  // useLayoutEffect(() => {
  //   const timeout = setTimeout(() => {
  //     document.body.style.overflow = 'hidden';
  //   }, 0); // let ResizeObserver finish
  
  //   return () => {
  //     clearTimeout(timeout);
  //     setTimeout(() => {
  //       document.body.style.overflow = '';
  //     }, 0);
  //   };
  // }, []);

  const validateEmail = (email: string, validationType: 'not_empty' | 'valid_email'): boolean => {
    if (!email.trim()) {
      setEmailError("Email is required");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email) && validationType === 'valid_email') {
      setEmailError("Please enter a valid email");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (!password.trim()) {
      setPasswordError("Password is required");
      return false;
    }
    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleEmailLogin = async (userEmail: string, userPassword: string) => {
    setLoading(true);

    // Track login attempt
    trackEvent(AUTH_EVENTS.LOGIN_ATTEMPTED, { method: 'email' });
    trackHyperDXAction('auth.login_attempt', { method: 'email' });

    try {
      const userCredential = await signInWithEmailAndPassword(auth, userEmail, userPassword);
      const checkRes: any = await checkUserExists(userCredential.user.uid); // ✅ awaits properly

      if (checkRes && checkRes.id > 0) {
        const status = checkRes.account_approval_status;
        if (status && status !== 'approved') {
          setAuthStatus(false);
          await auth.signOut();
          setFormError(
            status === 'pending'
              ? 'Your account request is under review. Please wait for admin approval.'
              : status === 'waitlisted'
                ? 'Your account request is on the waiting list.'
                : 'Your access request was not approved.'
          );
          trackEvent(AUTH_EVENTS.LOGIN_FAILED, { method: 'email', error_type: 'account_not_approved' });
          trackHyperDXAction('auth.login_failed', { method: 'email', errorCode: 'account_not_approved' });
          return;
        }
        //Set isAuthenticated to true in localStorage
        setAuthStatus(true);

        // Track successful login and set user ID
        trackEvent(AUTH_EVENTS.LOGIN_SUCCESS, { method: 'email' });
        setUserId(userCredential.user.uid);
        // HyperDX tracking
        setHyperDXUser(userCredential.user.uid, userEmail);
        trackHyperDXAction('auth.login_success', { method: 'email' });

        // Set user properties for segmentation (no PII)
        setUserProperties({
          user_role: checkRes.role || 'user',
          artist_count: checkRes.artist_count || 0,
        });

        navigate(config.DefaultRoutePage);
      } else {
        setAuthStatus(false);
        trackEvent(AUTH_EVENTS.LOGIN_FAILED, { method: 'email', error_type: 'user_not_found' });
        trackHyperDXAction('auth.login_failed', { method: 'email', errorCode: 'user_not_found' });
        await auth.signOut(); // 🔐 logout explicitly
      }
    } catch (error) {
      setAuthStatus(false);
      const authError = error as AuthError;
      trackEvent(AUTH_EVENTS.LOGIN_FAILED, { method: 'email', error_type: authError.code || 'unknown' });
      trackHyperDXAction('auth.login_failed', { method: 'email', errorCode: authError.code || 'unknown' });
      setFormError(getFirebaseErrorMessage(authError));
    } finally {
      setLoading(false);
    }
  };
  

  const handleAppleSignIn = async () => {
    setLoading(true);

    // Track login attempt
    trackEvent(AUTH_EVENTS.LOGIN_ATTEMPTED, { method: 'apple' });
    trackHyperDXAction('auth.login_attempt', { method: 'apple' });

    try {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      provider.setCustomParameters({ prompt: "consent" });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // 1. Check if user exists in your backend
      const checkRes: any = await checkUserExists(user.uid);
      //console.log("Apple CheckUser:", checkRes);

      if (checkRes && checkRes.id > 0) {
        // User exists, log them in
        setAuthStatus(true);
        trackEvent(AUTH_EVENTS.LOGIN_SUCCESS, { method: 'apple' });
        setUserId(user.uid);
        setHyperDXUser(user.uid, user.email || undefined);
        trackHyperDXAction('auth.login_success', { method: 'apple' });
        setUserProperties({
          user_role: checkRes.role || 'user',
          artist_count: checkRes.artist_count || 0,
        });
        navigate(config.DefaultRoutePage);
      } else {
        // User doesn't exist, create them automatically
        setSignupInProgress(true); // Mark signup as in progress BEFORE backend user creation
        trackEvent(AUTH_EVENTS.SIGNUP_METHOD_SELECTED, { method: 'apple' });

        if (!user.email) {
          throw new Error("Email not found in Apple account");
        }

        const newUser = await createUserIfNotExists({
          fbuid: user.uid,
          email: user.email,
          username: user.email,
          firstname: user.displayName?.split(' ')[0] || '',
          lastname: user.displayName?.split(' ').slice(1).join(' ') || '',
          artistname: user.displayName || user.email, // Use display name or email as artist name
          distributor: "", // Empty distributor for OAuth users - they can add later
        });

        if (newUser && newUser.id > 0) {
          setAuthStatus(true);
          setSignupInProgress(false); // Mark signup as complete
          trackEvent(AUTH_EVENTS.SIGNUP_SUCCESS, { method: 'apple' });
          setUserId(user.uid);
          setHyperDXUser(user.uid, user.email || undefined);
          trackHyperDXAction('auth.signup_success', { method: 'apple' });
          triggerProfileFetch(); // Manually trigger profile fetch after signup
          navigate(config.DefaultRoutePage);
        } else {
          setAuthStatus(false);
          setSignupInProgress(false); // Mark signup as failed
          trackEvent(AUTH_EVENTS.SIGNUP_ERROR, { method: 'apple', error_type: 'user_creation_failed' });
          trackHyperDXAction('auth.signup_error', { method: 'apple', errorCode: 'user_creation_failed' });
          await auth.signOut();
          console.error("Failed to create Apple user in database");
          setFormError("Failed to create user account. Please try again.");
        }
      }

    } catch (error) {
      setAuthStatus(false);
      setSignupInProgress(false); // Mark signup as failed
      const authError = error as AuthError;
      trackEvent(AUTH_EVENTS.LOGIN_FAILED, { method: 'apple', error_type: authError.code || 'unknown' });
      trackHyperDXAction('auth.login_failed', { method: 'apple', errorCode: authError.code || 'unknown' });
      console.error("Apple Sign-In Error:", authError);
      setFormError(getFirebaseErrorMessage(authError));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    const provider = new GoogleAuthProvider();

    // Track login attempt
    trackEvent(AUTH_EVENTS.LOGIN_ATTEMPTED, { method: 'google' });
    trackHyperDXAction('auth.login_attempt', { method: 'google' });

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // 1. Check if user exists in your backend
      const checkRes: any = await checkUserExists(user.uid);
      //console.log("CheckUser:", checkRes);

      if (checkRes && checkRes.id > 0) {
        const status = checkRes.account_approval_status;
        if (status && status !== 'approved') {
          setAuthStatus(false);
          await auth.signOut();
          setFormError(
            status === 'pending'
              ? 'Your account request is under review. Please wait for admin approval.'
              : status === 'waitlisted'
                ? 'Your account request is on the waiting list.'
                : 'Your access request was not approved.'
          );
          return;
        }
        // User exists, log them in
        setAuthStatus(true);
        trackEvent(AUTH_EVENTS.LOGIN_SUCCESS, { method: 'google' });
        setUserId(user.uid);
        setHyperDXUser(user.uid, user.email || undefined);
        trackHyperDXAction('auth.login_success', { method: 'google' });
        setUserProperties({
          user_role: checkRes.role || 'user',
          artist_count: checkRes.artist_count || 0,
        });
        navigate(config.DefaultRoutePage);
      } else {
        // New Google accounts must request access via /signup (no open auto-create).
        setAuthStatus(false);
        await auth.signOut();
        setFormError('No Nova account found. Please request access from the Sign up page.');
        trackEvent(AUTH_EVENTS.LOGIN_FAILED, { method: 'google', error_type: 'account_not_found' });
        trackHyperDXAction('auth.login_failed', { method: 'google', errorCode: 'account_not_found' });
      }

    } catch (error) {
      setAuthStatus(false);
      setSignupInProgress(false); // Mark signup as failed
      const authError = error as AuthError;
      trackEvent(AUTH_EVENTS.LOGIN_FAILED, { method: 'google', error_type: authError.code || 'unknown' });
      trackHyperDXAction('auth.login_failed', { method: 'google', errorCode: authError.code || 'unknown' });
      setFormError(getFirebaseErrorMessage(authError));
      console.error("Google login error:", authError);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to sync Firebase user with Supabase
  // const syncUserWithSupabase = async (user: User) => {
  //   if (!user || !user.uid || !user.email) return;
    
  //   try {
  //     // Extract user details from Firebase user
  //     const userData = {
  //       fbuid: user.uid,
  //       email: user.email,
  //       firstname: user.displayName?.split(' ')[0] || '',
  //       lastname: user.displayName?.split(' ').slice(1).join(' ') || '',
  //       username: ""
  //     };
      
  //     // Create user in Supabase if they don't exist
  //     await createUserIfNotExists(userData);
  //   } catch (error) {
  //     console.error("Error syncing user with Supabase:", error);
  //     // Don't block the login flow if this fails
  //   }
  // };

  const AppleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path d="M16.365 0c-.846.038-1.854.6-2.451 1.308-.53.62-.99 1.57-.814 2.5.947.065 1.92-.487 2.485-1.183.57-.707.985-1.642.78-2.625zM21.963 17.78c-.55 1.364-.8 1.88-1.5 3.035-.98 1.57-2.37 3.526-4.185 3.534-1.66.015-2.08-1.026-4.32-1.02-2.23.007-2.7 1.033-4.36 1.018-1.81-.007-3.28-1.773-4.26-3.344C1.665 18.94.598 14.683 2.39 11.363c1.15-2.123 2.965-3.357 4.76-3.37 1.84-.015 3.01 1.132 4.52 1.163 1.46.03 2.37-1.168 4.51-1.188 1.56-.014 3.2.85 4.37 2.326-3.82 2.11-3.17 7.68-.59 9.485z"/>
    </svg>
  );

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    
    // Validate password first
    const isPasswordValid = validatePassword(password);
    if (!isPasswordValid) {
      return;
    }

    const isEmailValid = validateEmail(email, 'valid_email');
    if (!isEmailValid) {
      return;
    }
    await handleEmailLogin(email, password);
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center px-4 sm:px-6 bg-nova-color">
      <form onSubmit={handleFormSubmit} className="w-full max-w-md flex flex-col items-center space-y-6 p-6 bg-nova-color login-form mt-8 sm:mt-32">
        
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
        <img src="/assets/images/Nova.png" alt="Nova" className="h-10 w-auto mb-6 sm:mb-8" />
        </Link>

        {/* Email Input */}
        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="text"
            placeholder="Email"
            className={`w-full border-b ${emailError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
            style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
            value={email}
            onChange={(e) => {
              const value = e.target.value;
              setEmail(value);
              if (emailError) validateEmail(value, 'not_empty');
            }}
          />
          {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
        </div>

        {/* Password Input */}
        <div className="w-full sm:w-[25.8125rem]">
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className={`w-full border-b ${passwordError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 pr-10 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans-regular`}
              style={{ 
                backgroundColor: '#F8F7F2', 
                '--tw-placeholder-color': '#939393',
                fontSize: '0.875rem',
                letterSpacing: showPassword || !password ? 'normal' : '0.125rem'
              } as React.CSSProperties}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) validatePassword(e.target.value);
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {passwordError && <p className="text-red-500 text-xs mt-1">{passwordError}</p>}
          <div className="w-full text-right mt-2">
            <Link to="/forgot-password" className="text-sm text-[#939393] hover:underline" style={{ fontSize: "0.625rem" }}>
              Forgot Password?
            </Link>
          </div>
        </div>
        
        {/* Form Error Message */}
        {formError && <div className="w-full sm:w-[25.8125rem] text-red-500 text-xs mt-1">{formError}</div>}

        {/* Sign In Button */}
        <button
          type="submit"
          className="w-full sm:w-[25.8125rem] border px-5 py-3 sm:py-4 rounded-full text-sm sm:text-base text-black font-medium hover:bg-gray-200 transition flex items-center justify-center gap-3 uppercase min-h-[3rem] navbar-menu-item"
          style={{ paddingTop: "1rem" }}
          disabled={loading}
        >
          {loading ? (
        <div className="h-5 w-5 animate-spin border-2 border-t-transparent border-black rounded-full" />
      ) : (
        <>
          {/* <img src="/assets/images/email-icon.svg" alt="Email Icon" className="h-5 w-5" />
          Sign In with Email */}
          Sign In
        </>
      )}
        </button>

        {/* Divider */}
        {/* <div className="w-full sm:w-[25.5625rem] h-[0.1875rem] bg-nova-divider-200"></div> */}

        {/* Sign in with Apple */}
        {/* <button onClick={handleAppleSignIn} className="w-full sm:w-[25.8125rem] border px-5 py-3 sm:py-4 rounded-full flex items-center justify-center text-sm sm:text-base text-black font-medium hover:bg-gray-200 transition gap-3 uppercase min-h-[3rem]">
          <img src="/assets/images/apple-icon-3.svg" alt="Apple Icon" className="h-5 w-5" />
          <span style={{ paddingTop: "6px" }}> Continue with Apple</span>
        </button> */}

        {/* Sign in with Google */}
        {/* <button
          onClick={handleGoogleLogin}
          className="w-full sm:w-[25.8125rem] border px-5 py-3 sm:py-4 rounded-full flex items-center justify-center text-sm sm:text-base text-black font-medium hover:bg-gray-200 transition gap-3 uppercase min-h-[3rem]"
          style={{ paddingTop: "1rem" }}
        >
          <img src="/assets/images/google-icon-2.svg" alt="Google Logo" className="h-5 w-5" />
          Continue with Google
        </button> */}

        {/* Join Waitlist */}
        {/* <button className="w-full sm:w-[25.8125rem] bg-black text-white px-5 py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium hover:bg-gray-800 transition uppercase min-h-[3rem]">
          Sign Up
        </button> */}
        <div className="w-full text-center mt-2">
            <Link to="/signup" className="text-sm text-[#939393] hover:underline font-family-messina-sans-regular" style={{ fontSize: "0.625rem" }}>
              Not on Nova Yet? Sign up
            </Link>
          </div>
      </form>
    </div>
  );
};

export default LoginForm;
